/**
 * Camera Error Boundary
 * Handles camera-specific errors with helpful recovery suggestions
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AppContainer } from './AppContainer';
import { VerticalStack } from './VerticalStack';
import { ScreenTitle } from './ScreenTitle';
import { ScreenDescription } from './ScreenDescription';
import { PrimaryButton } from './PrimaryButton';
import { SecondaryButton } from './SecondaryButton';
import { IconDisplay } from './IconDisplay';
import { List } from './List';
import { logger } from '../../utils/logger';
import { getCameraErrorType } from '../../utils/cameraErrorHandler';
import { LanguageContext, tUnsafe } from '../../i18n';

const cameraErrorLogger = logger.clone({ labels: { component: 'CameraErrorBoundary' }});

interface CameraErrorBoundaryProps {
  children: ReactNode;
  onRetry?: () => void;
  onSkipCamera?: () => void;
}

interface CameraErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorType:
    | 'permission'
    | 'security'
    | 'not-found'
    | 'in-use'
    | 'unsupported'
    | 'stalled'
    | 'interrupted'
    | 'unknown'
    | null;
}

export class CameraErrorBoundary extends Component<
  CameraErrorBoundaryProps,
  CameraErrorBoundaryState
> {
  static contextType = LanguageContext;

  constructor(props: CameraErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorType: null,
    };
  }

  static getDerivedStateFromError(
    error: Error
  ): Partial<CameraErrorBoundaryState> {
    const errorType = getCameraErrorType(error);

    return {
      hasError: true,
      error,
      errorType,
    };
  }

  componentDidCatch(error: Error, _errorInfo: ErrorInfo) {
    cameraErrorLogger.error('Camera error caught', error instanceof Error ? error : new Error(`${error}`));
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorType: null,
    });

    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  handleSkipCamera = () => {
    if (this.props.onSkipCamera) {
      this.props.onSkipCamera();
    }
  };

  getErrorContent() {
    const { errorType } = this.state;

    switch (errorType) {
      case 'permission':
        return {
          title: tUnsafe('cameraErrorBoundary.permission.title'),
          description: tUnsafe('cameraErrorBoundary.permission.description'),
          steps: [
            tUnsafe('cameraErrorBoundary.permission.step1'),
            tUnsafe('cameraErrorBoundary.permission.step2'),
            tUnsafe('cameraErrorBoundary.permission.step3'),
          ],
          showSkip: false,
        };

      case 'security':
        return {
          title: tUnsafe('verificationErrorBoundary.cameraDenied.title'),
          description: tUnsafe('cameraErrors.security'),
          steps: [tUnsafe('cameraErrors.securityAction')],
          showSkip: false,
        };

      case 'not-found':
        return {
          title: tUnsafe('cameraErrorBoundary.notFound.title'),
          description: tUnsafe('cameraErrorBoundary.notFound.description'),
          steps: [
            tUnsafe('cameraErrorBoundary.notFound.step1'),
            tUnsafe('cameraErrorBoundary.notFound.step2'),
            tUnsafe('cameraErrorBoundary.notFound.step3'),
          ],
          showSkip: true,
        };

      case 'in-use':
        return {
          title: tUnsafe('cameraErrorBoundary.inUse.title'),
          description: tUnsafe('cameraErrorBoundary.inUse.description'),
          steps: [
            tUnsafe('cameraErrorBoundary.inUse.step1'),
            tUnsafe('cameraErrorBoundary.inUse.step2'),
            tUnsafe('cameraErrorBoundary.inUse.step3'),
          ],
          showSkip: false,
        };

      case 'unsupported':
        return {
          title: tUnsafe('cameraErrorBoundary.unsupported.title'),
          description: tUnsafe('cameraErrorBoundary.unsupported.description'),
          steps: [
            tUnsafe('cameraErrorBoundary.unsupported.step1'),
            tUnsafe('cameraErrorBoundary.unsupported.step2'),
            tUnsafe('cameraErrorBoundary.unsupported.step3'),
          ],
          showSkip: true,
        };

      default:
        return {
          title: tUnsafe('cameraErrorBoundary.default.title'),
          description: tUnsafe('cameraErrorBoundary.default.description'),
          steps: [
            tUnsafe('cameraErrorBoundary.default.step1'),
            tUnsafe('cameraErrorBoundary.default.step2'),
            tUnsafe('cameraErrorBoundary.default.step3'),
          ],
          showSkip: true,
        };
    }
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const { title, description, steps, showSkip } = this.getErrorContent();

      return (
        <AppContainer maxWidth="content">
          <VerticalStack spacing="lg" align="center">
            <IconDisplay
              src="/icons/camera.svg"
              alt={tUnsafe('cameraErrorBoundary.iconAlt')}
              size="xl"
            />

            <VerticalStack spacing="md" align="center">
              <ScreenTitle>{title}</ScreenTitle>
              <ScreenDescription>{description}</ScreenDescription>
            </VerticalStack>

            {steps.length > 0 && (
              <VerticalStack spacing="sm" style={{ width: '100%' }}>
                <ScreenDescription>
                  {tUnsafe('cameraErrorBoundary.trySteps')}
                </ScreenDescription>
                <List items={steps} ordered />
              </VerticalStack>
            )}

            {/* Platform-specific help */}
            {this.state.errorType === 'permission' && (
              <VerticalStack spacing="xs" style={{ width: '100%' }}>
                <ScreenDescription style={{ fontSize: '0.875rem' }}>
                  <strong>{tUnsafe('cameraErrorBoundary.platform.chrome')}</strong>{' '}
                  {tUnsafe('cameraErrorBoundary.platform.chromeInstruction')}
                </ScreenDescription>
                <ScreenDescription style={{ fontSize: '0.875rem' }}>
                  <strong>{tUnsafe('cameraErrorBoundary.platform.firefox')}</strong>{' '}
                  {tUnsafe('cameraErrorBoundary.platform.firefoxInstruction')}
                </ScreenDescription>
                <ScreenDescription style={{ fontSize: '0.875rem' }}>
                  <strong>{tUnsafe('cameraErrorBoundary.platform.safari')}</strong>{' '}
                  {tUnsafe('cameraErrorBoundary.platform.safariInstruction')}
                </ScreenDescription>
              </VerticalStack>
            )}

            <VerticalStack spacing="sm" style={{ width: '100%' }}>
              <PrimaryButton onClick={this.handleRetry} fullWidth>
                {tUnsafe('common.tryAgain')}
              </PrimaryButton>

              {showSkip && this.props.onSkipCamera && (
                <SecondaryButton onClick={this.handleSkipCamera} fullWidth>
                  {tUnsafe('cameraErrorBoundary.skipCamera')}
                </SecondaryButton>
              )}
            </VerticalStack>
          </VerticalStack>
        </AppContainer>
      );
    }

    return this.props.children;
  }
}
